import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import {
  batchCreateBasicField,
  batchCreateEvent,
  batchCreateEventType,
  batchCreateRelationship,
  batchDeleteElementAndSubs,
  batchEditEventType,
  batchMovePersonToRelationshipRecord,
  createFieldValue,
  createStandardizedFieldValue,
  getImage,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getOtherRelationshipSuperElements,
  getFirstArkInImageOrder,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../../shared/services/config'
import {
  getOrCreateSuperIdFromFormState,
  replaceRelationshipPerson,
} from '../../../../../components/generic/panels/eventPanel/utils/elementUtils'

// Function to dedupe an array of any-shaped objects using the lodash isEqual function
export const superDeduper = (arr) => {
  const deduped = []
  arr.forEach((obj) => {
    if (!deduped.find((d) => isEqual(d, obj))) {
      deduped.push(obj)
    }
  })
  return deduped
}

export const ensureRelPeopleAreInSameRecord = (
  images,
  selectedPerson,
  movingPersonToOtherRecord,
  otherPerson,
  keepThisRelationshipId
) => {
  let dispatches = []

  if (otherPerson.recordId !== selectedPerson.recordId) {
    // If we're moving the selected person to another record, that is the only change we should process
    if (movingPersonToOtherRecord) {
      dispatches = batchMovePersonToRelationshipRecord({
        mainPerson: selectedPerson,
        otherPerson,
        images,
        movingMainPersonToOtherRecord: movingPersonToOtherRecord,
        keepThisRelationshipId,
      })
    } else {
      dispatches = dispatches.concat(
        batchMovePersonToRelationshipRecord({
          mainPerson: selectedPerson,
          otherPerson,
          images,
          movingMainPersonToOtherRecord: movingPersonToOtherRecord,
          keepThisRelationshipId,
        })
      )
    }
  }
  return dispatches
}

export function createRelationship({ relationship, selectedPerson, otherPerson, thumbnails, selectedPersonId }) {
  // Put the relationship on the image that comes first if two are involved
  const adjustedSelectedPersonPartitionKey = getFirstArkInImageOrder(
    thumbnails,
    selectedPerson.partitionKey,
    otherPerson.partitionKey
  )
  return batchCreateRelationship({
    partitionKey: adjustedSelectedPersonPartitionKey,
    person1ID: relationship.order === 'FIRST' ? relationship.value.key : selectedPersonId,
    person2ID: relationship.order === 'FIRST' ? selectedPersonId : relationship.value.key,
    person1PartitionKey: relationship.order === 'FIRST' ? otherPerson.partitionKey : selectedPerson.partitionKey,
    person2PartitionKey: relationship.order === 'FIRST' ? selectedPerson.partitionKey : otherPerson.partitionKey,
    relType: relationship.relType,
    id: relationship.id,
  })
}

export function createOrEditRelationship({
  relationship,
  relationshipFields,
  images,
  selectedPerson,
  movingPersonToOtherRecord,
  thumbnails,
  selectedPersonId,
  temporarySlsPersons,
}) {
  let dispatches = []
  const relationshipField = relationshipFields.find((rf) => rf.id === relationship.id)
  const otherPerson =
    temporarySlsPersons[relationship.value.key] || getNestedOrFloatingElementById(images, relationship.value.key)

  if (otherPerson) {
    // Handle moving people to same record if needed
    dispatches = dispatches.concat(
      ensureRelPeopleAreInSameRecord(
        images,
        selectedPerson,
        movingPersonToOtherRecord,
        otherPerson,
        relationshipField?.id
      )
    )

    if (relationshipField) {
      // Edit existing relationship
      dispatches = dispatches.concat(
        replaceRelationshipPerson({
          relationshipField,
          otherPerson,
          selectedPersonId,
          value: relationship.value,
          order: relationship.order,
        })
      )
    } else if (relationship.relType !== 'OTHER') {
      // Add new relationship
      dispatches = dispatches.concat(
        createRelationship({ relationship, selectedPerson, otherPerson, thumbnails, selectedPersonId })
      )
    }
  }
  return dispatches
}

/**
 * Generates dispatches for the editRelationships panel, based on a { relationships: <useFieldArray> } form structure
 * @param {Object} values the form values
 * @param {Array} images images from SLS state
 * @param {String} selectedPersonId the selected person's ID
 * @param {Array} thumbnails thumbnails from shared browser state
 * @param {Object} formState formState tracked by ZionForm, including defaultValues
 * @param {Boolean} movingPersonToOtherRecord whether the selected person is being moved to the other person's record
 * @param {Object} temporarySlsPersons temporary persons created in R2 for relationships - these will be added as people during the save process
 * @returns {*[]} Dispatches to be processed by the SLS reducer
 */
export const submitEditRelationships = ({
  values,
  images,
  selectedPersonId,
  thumbnails,
  formState,
  movingPersonToOtherRecord,
  temporarySlsPersons = {},
}) => {
  const selectedPerson = getNestedOrFloatingElementById(images, selectedPersonId)
  const relationshipFields = getNestedSubelementsByType(selectedPerson, 'RELATIONSHIP')
  let dispatches = []
  values.relationships.forEach((relationship) => {
    dispatches = dispatches.concat(
      createOrEditRelationship({
        relationship,
        relationshipFields,
        images,
        selectedPerson,
        movingPersonToOtherRecord,
        thumbnails,
        selectedPersonId,
        temporarySlsPersons,
      })
    )
  })

  // Delete removed relationships
  formState.defaultValues?.relationships?.forEach((defaultRel) => {
    if (!values.relationships.some((rel) => rel.id === defaultRel.id)) {
      const relationshipField = relationshipFields.find((rf) => rf.id === defaultRel.id)

      if (relationshipField) {
        dispatches = dispatches.concat(
          batchDeleteElementAndSubs({
            element: relationshipField,
          })
        )
      }
    }
  })

  // Remove duplicate dispatches
  const dedupedDispatches = superDeduper(dispatches)
  // Move the SET_RECORD_PERSON_IDS dispatch to the end of the array since the deduping function removes all but the first instance
  dedupedDispatches.push(
    dedupedDispatches.splice(
      dedupedDispatches.findIndex((d) => d.type === 'SET_RECORD_PERSON_IDS'),
      1
    )[0]
  )
  return dedupedDispatches
}

export const editEventType = ({ attribution, el, value, eventTypeFieldId, types }) => {
  // The event has to be deleted and recreated, and the event type field has to have a new field value created.
  const eventTypeField = el?.subElements?.find((se) => se.fieldType === 'EVENT_TYPE')
  const eventType = types?.events?.find((ev) => ev.eventType === value)
  const eventTypeLabel = eventType?.displayLabel
  const typeFieldValue = createFieldValue(eventTypeLabel, attribution, eventType?.uri)

  const hasEventTypeField = !isEmpty(eventTypeField)
  if (hasEventTypeField) {
    return batchEditEventType({
      partitionKey: el.partitionKey,
      typeFieldValue,
      typeFieldId: eventTypeField.id,
    })
  }
  // doesn't have event type field
  return batchCreateEventType({
    partitionKey: el.partitionKey,
    typeFieldId: eventTypeFieldId,
    typeFieldValue,
    elementId: el.id,
  })
}

const createEvent = ({
  values,
  selectedPerson,
  eventToAdd,
  attribution,
  types,
  selectedPersonId,
  addedEventId,
  eventTypeFieldId,
}) => {
  let dispatches = []
  const eventType = types?.events?.find((ev) => ev.eventType === values[addedEventId])
  const eventTypeLabel = eventType?.displayLabel
  const eventTypeFieldValue = createFieldValue(eventTypeLabel, attribution, eventType?.uri)
  let createDate = false
  let createPlace = false
  // eslint-disable-next-line no-restricted-syntax
  for (const [, value] of Object.entries(values)) {
    if (value === 'DATE') createDate = true
    if (value === 'PLACE') createPlace = true
  }
  const eventDispatches = batchCreateEvent({
    partitionKey: selectedPerson.partitionKey,
    eventType: values[addedEventId],
    personId: selectedPersonId,
    id: addedEventId,
    eventTypeFieldValue,
    createDate,
    createPlace,
    eventTypeId: eventTypeFieldId,
  })
  const dateId = eventDispatches.find(
    (action) => action.type === 'CREATE_ELEMENT' && action.value.elementType === 'DATE'
  )?.value.id
  const placeId = eventDispatches.find(
    (action) => action.type === 'CREATE_ELEMENT' && action.value.elementType === 'PLACE'
  )?.value.id
  // Remember that we added this event in case we need to add fields to it
  if (createDate) eventToAdd.dateId = dateId
  if (createPlace) eventToAdd.placeId = placeId
  dispatches = dispatches.concat(eventDispatches)
  return dispatches
}

export const getFieldIdAndValue = ({ el, value, attribution }) => {
  let field
  if (el.elementType === 'FIELD') field = el
  else field = el?.subElements?.length ? el?.subElements?.[0] : el
  let fieldValue = createFieldValue(typeof value === 'string' ? value : value.originalText, attribution)
  if (value.standard) {
    // place or date fields
    fieldValue = createStandardizedFieldValue(value, field.fieldType, attribution)
  }
  if (field.fieldType === 'SEX_CODE') {
    fieldValue = createFieldValue(value.primaryText, attribution, `http://gedcomx.org/${value.key}`)
  }
  return { id: field.id, value: fieldValue }
}

export const removeFieldOrEvent = ({ key, el }) => {
  const dispatches = []
  dispatches.push({
    type: 'REMOVE_ELEMENT',
    value: {
      id: key,
      partitionKey: el.partitionKey,
      elementType: el.elementType,
    },
  })
  if (el.elementType === 'EVENT') {
    const eventTypeField = el?.subElements?.find((se) => se.fieldType === 'EVENT_TYPE')
    if (eventTypeField) {
      dispatches.push({
        type: 'REMOVE_ELEMENT',
        value: {
          id: eventTypeField.id,
          partitionKey: el.partitionKey,
          elementType: eventTypeField.elementType,
        },
      })
    }
  }
  return dispatches
}

export const submitEditEvents = ({
  values,
  images,
  selectedPersonId,
  formState,
  types,
  addedEventId,
  eventTypeFieldId,
  currentImageArk,
  movingPersonToOtherRecord,
}) => {
  const currentImage = getImage(images, currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], selectedPersonId)

  const eventElements = selectedPerson?.subElements.filter((se) => se.elementType === 'EVENT') || []
  const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: R2AppName }

  const eventToAdd = addedEventId ? {} : null
  let dispatches = addedEventId
    ? createEvent({
        values,
        selectedPerson,
        eventToAdd,
        attribution,
        types,
        selectedPersonId,
        addedEventId,
        eventTypeFieldId,
      })
    : []

  Object.entries(values).forEach(([key, value]) => {
    const el = getNestedOrFloatingElementById([selectedPerson], key)
    if (el) {
      if (value === 'REMOVED') {
        dispatches = [...dispatches, ...removeFieldOrEvent({ key, el })]
      } else if (el.elementType === 'RELATIONSHIP') {
        if (value) {
          const otherPerson = getNestedOrFloatingElementById(images, value.key)
          const relationshipDispatches = [
            ensureRelPeopleAreInSameRecord(images, selectedPerson, movingPersonToOtherRecord, otherPerson, el.id),
            el.superElements.length > 1
              ? replaceRelationshipPerson({
                  relationshipField: el,
                  otherPerson,
                  selectedPersonId,
                  value,
                  order: values[`${key}_order`],
                })
              : [
                  // Relationship exists but only has one person connected to it
                  {
                    type: 'ADD_SUB',
                    value: {
                      superId: otherPerson.id,
                      subId: el.id,
                      superPartitionKey: otherPerson.partitionKey,
                      subPartitionKey: el.partitionKey,
                      elementType: 'PERSON',
                      extraArgs: {
                        order: values[`${key}_order`],
                      },
                    },
                  },
                ],
          ].flat()

          dispatches = dispatches.concat(relationshipDispatches)
        } else {
          dispatches.push({
            type: 'REM_SUB',
            value: {
              superId: getOtherRelationshipSuperElements(el, selectedPerson)?.[0].id,
              subId: el.id,
              superPartitionKey: getOtherRelationshipSuperElements(el, selectedPerson)?.[0].partitionKey,
              subPartitionKey: el.partitionKey,
              elementType: 'PERSON',
            },
          })
        }
      } else if (
        el.elementType === 'EVENT' &&
        (formState.dirtyFields[key] || !el?.subElements?.find((se) => se.fieldType === 'EVENT_TYPE'))
      ) {
        dispatches = [...dispatches, ...editEventType({ attribution, el, value, eventTypeFieldId, types })]
      } else if (formState.dirtyFields[key]) {
        const { id, value: fieldValue } = getFieldIdAndValue({ el, value, attribution })
        if (fieldValue) {
          dispatches.push({ type: 'ADD_FV', value: { partitionKey: el.partitionKey, id, fieldValue } })
        }
      }
    } else if (
      !key.includes('fieldType') &&
      !key.includes('super') &&
      !key.includes('order') &&
      addedEventId !== key &&
      value !== 'REMOVED'
    ) {
      let fieldValue = createFieldValue(typeof value === 'string' ? value : value?.originalText || '', attribution)
      if (value.standard) {
        fieldValue = createStandardizedFieldValue(value, values[`${key}_fieldType`], attribution)
      }
      const {
        superId,
        superType,
        dispatches: superDispatches,
      } = getOrCreateSuperIdFromFormState({
        values,
        key,
        eventToAdd,
        eventElements,
        selectedPerson,
      })

      dispatches = [
        ...dispatches,
        ...superDispatches,
        ...batchCreateBasicField({
          partitionKey: selectedPerson.partitionKey,
          superId,
          fieldType: values[`${key}_fieldType`],
          fieldValue,
          superElementType: superType,
          id: key,
        }),
      ]
    }
  })
  return dispatches
}
